<i18n>
{
  "fr": {
    "tool-title": "Information sur le point d'eau",
    "no-data-title": "Aucune donnée",
    "oid": "Identifiant DB",
    "publicId": "Identifiant REGIS",
    "name": "Nom",
    "timeseries-title": "Mesures disponibles",
    "measured-parameter": "Paramètre mesuré",
    "data-since": "Première donnée",
    "piezo-title": "Piézomètre | Piézomètres",
    "screen": "Crépine | Crépine | Crépines",
    "regisId": "ID REGIS",
    "type": "Type d'ouvrage",
    "status": "Etat de l'ouvrage",
    "external-diameter": "Diamètre (inches)",
    "casing": "Tubage",
    "selected-piezometer": "Piézomètre",
    "goto-data": "Accéder aux données",
    "current-end-depth": "Prof. courante",
    "original-end-depth": "Prof. originale"
  },
  "de": {

  }
}
</i18n>

<template>
  <div id="piezo-info" class="tool" v-if="show">
    <h3>{{ $tc('piezo-title', piezoDescriptions.length) }}</h3>
    <md-content class="md-scrollbar main-container">
      <div v-if="!loading">
        <md-field v-if="piezoDescriptions.length > 1">
          <label for="selectedPiezometer">{{ $t('selected-piezometer') }}</label>
          <md-select v-model="selectedPiezometerKey" name="selectedPiezometer">
            <md-option v-for="piezoDesc in piezoDescriptions" :key="getPiezometerKey(piezoDesc)" :value="getPiezometerKey(piezoDesc)">
              {{ piezoDesc.wellDbk }}.{{ piezoDesc.seq }}
            </md-option>
          </md-select>
        </md-field>
        <div class="piezo-description">
          <div class="info">
            <h4>{{ $t('name') }}</h4>
            <span class="info-value">{{ selectedPiezometer.piezometerId }}</span>
          </div>

          <div class="info">
            <h4>{{ $t('type') }}</h4>
            <span class="info-value">{{ selectedPiezometer.type }}</span>
          </div>

          <div class="info">
            <h4>{{ $t('status') }}</h4>
            <span class="info-value">{{ selectedPiezometer.status | formatNullValue }}</span>
          </div>

          <div class="info">
            <h4>{{ $t('current-end-depth') }}</h4>
            <span class="info-value">{{ wellDescription.currentEnddepth | formatDepth }}</span>
          </div>

          <div class="info">
            <h4>{{ $t('original-end-depth') }}</h4>
            <span class="info-value">{{ wellDescription.originalEnddepth | formatDepth }}</span>
          </div>

          <div class="info">
            <h4>{{ $t('external-diameter') }}</h4>
            <span class="info-value">{{ selectedPiezometer.externalDiameterInches | formatExternalDiameter }}</span>
          </div>

          <div class="info">
            <h4>{{ $t('casing') }}</h4>
            <span class="info-value">{{ selectedPiezometer.casing | formatNullValue }}</span>
          </div>

          <div class="info">
            <h4>{{ $tc('screen', selectedPiezometer.screens.length) }}</h4>
            <span class="info-value" v-if="selectedPiezometer.screens.length === 0">-</span>
            <span class="info-value" v-for="screen in selectedPiezometer.screens" :key="`${screen.wellDbk}_${screen.seq}_${screen.screenSeq}`">
              <b>{{ screen.screenTop | formatDepth }} - {{ screen.screenBottom | formatDepth }}</b>
            </span>
          </div>

          <md-button class="show-timeseries md-raised" @click="gotoTimeseries(selectedPiezometer.stratesDbk)">{{ $t('goto-data') }}</md-button>
        </div>
      </div>

      <div class="loading" v-if="loading">
        <md-progress-spinner class="loading-animation" :md-diameter="100" :md-stroke="5" md-mode="indeterminate"></md-progress-spinner>
      </div>

    </md-content>
  </div>
</template>

<script>
import _ from 'lodash'

import ObservationPointProxy from '@/proxies/observationPoint'
import MapStoreMixin from '@/mixins/store/map'
import LayerStoreMixin from '@/mixins/store/layer'
import ToolStoreMixin from '@/mixins/store/tool'

export default {
  name: 'PiezoDescription',
  mixins: [
    MapStoreMixin,
    LayerStoreMixin,
    ToolStoreMixin
  ],
  data () {
    return  {
      loading: true,
      wellDescription: {},
      piezoDescriptions: [],
      selectedPiezometerKey: ''
    }
  },
  mounted () {
    this.$nextTick(() => {
      this.updateTool()
    })
  },
  unmounted() {
    this.removeTool()
  },
  watch: {
    currentObject () {
      this.updateTool()
    }
  },
  computed: {
    show () {
      return !_.isEmpty(this.currentObject) && this.currentGeom !== null && !this.loading
    },
    selectedPiezometer () {
      return this.piezoDescriptions.find((piezoDesc) => this.getPiezometerKey(piezoDesc) === this.selectedPiezometerKey)
    }
  },
  filters: {
    formatNullValue (value) {
      if (value === null) return '-'
      return value
    },
    formatDepth (value) {
      if (value === null || value === '999.99' || value === 999.99) return '-'
      return `${value.toFixed(2)}`
    },
    formatExternalDiameter (value) {
      if (value === null || value === '999.99' || value === 999.99) return '-'
      return `${value.toFixed(1)}`
    }
  },
  methods: {
    updateTool () {
      if (!_.isEmpty(this.currentObject)) this.load()
      else this.removeTool()
    },
    removeTool () {

    },
    load () {
      this.loading = true
      this.piezoDescriptions = []
      this.compute().catch(this.handleError).finally(() => this.loading = false)
    },
    compute () {
      if (_.isEmpty(this.currentObject)) return
      return new Promise((resolve, reject) => {
        ObservationPointProxy.getRegisIds(this.currentObject.id).then((regisIds) => {
          let toFetch = [
            ObservationPointProxy.getWellDesc(regisIds.wellDbk),
            ObservationPointProxy.getPiezoDesc(regisIds.wellDbk)
          ]

          Promise.all(toFetch).then((data) => {
            this.wellDescription = data[0]
            this.piezoDescriptions = data[1]
            if (this.piezoDescriptions.length > 0) this.selectedPiezometerKey = this.getPiezometerKey(_.head(this.piezoDescriptions))
            resolve()
          }).catch(reject)
        }).catch(reject)
      })
    },
    getPiezometerKey (piezoDesc) {
      return `pdesc_${piezoDesc.wellDbk}_${piezoDesc.seq}`
    },
    gotoTimeseries (id) {
      let routeData = this.$router.resolve({name: 'observation-point-data', params: { id }});
      window.open(routeData.href, '_blank');
    },
    handleError (e) {
      console.log(e)
    }
  }
}



</script>

<style lang="scss">
  #piezo-info {
    width: 100%;

    .close-button {
      display: block;
      margin-left: auto;
      margin-right: 15px;
      margin-bottom: -30px;
      margin-top: 5px;
    }

    .main-container {
      overflow-y: auto;
      overflow-x: hidden;
      background: none;

      padding-left: 23px;
      padding-right: 23px;

      @media (max-height: 500px) {
        max-height: 100vh;
      }
    }


    .layer-name {
      font-size: 14px;
    }

    .container {
      margin-left: -10px;
      margin-right: -10px;
      background-color: white;

      h2 {
        margin-top: 5px;
      }
    }

    .show-timeseries {
      margin-top: 10px;
      width: 100%;
    }

    .empty-data {
      margin-top: 15px;
      margin-bottom: 15px;

      .md-icon {
        width: 80px;
        min-width: 80px;
        font-size: 80px !important;
      }
      .md-empty-state-label {
        font-size: 18px;
        line-height: 18px;
      }

      &.md-empty-state-leave-active {
        display: none !important;
      }
    }

    h4 {
      margin: 0px;
      margin-bottom: 5px;
      font-size: 13px;
    }

    .piezo-description {
      margin-top: 15px;
    }

    .info {
      display: flex;
      align-items: center;
      margin-top: 7px;

      &.piezo-id {
        justify-content: center;
        background: #e5e5e5;
      }

      h4 {
        min-width: 140px;
      }

      .info-value {
        margin-left: 5px;
      }
    }
  }
</style>
